import React from "react";

import styles from "./OnboardingRequirements.module.scss";

export interface RequirementItem {
  description: React.ReactNode;
  nested?: React.ReactNode[];
}

export const REQUIREMENT_TEXTS = {
  businessWebsite: "Business website (if available)",
  operatingAddressProof:
    "Proof of address (if operating address is different from the registered address)",
  individualShareholderIds: (
    <span>
      Passport/National IDs{" "}
      <span className={styles.boldUnderlineText}>and</span> Proof of Address of{" "}
      <span className={styles.boldUnderlineText}>all</span> individual
      shareholders who ultimately own 25% or more shares (directly or
      indirectly) or <span className={styles.boldUnderlineText}>all</span>{" "}
      individuals who exercises effective control of your company
    </span>
  ),
  individualShareholderAddress:
    "Registered address of the individuals with 25% or more ownership",
  individualShareholderAddressProof: "Proof of address of the individual",
  corporateShareholderDocument:
    "ACRA Bizfile or equivalent Company extract of corporate shareholders who own 25% or more shares in your company",
  corporateShareholderDocumentAU:
    "ASIC Extract or equivalent of corporate shareholders who own 25% or more shares in your company.",
  corporateShareholderDocumentHK:
    "Form NAR1 or equivalent of corporate shareholders who own 25% or more shares in your company",
  corporateShareholderDocumentMY:
    "SSM Extract or equivalent of corporate shareholders who own 25% or more in your company",
  letterOfAuthorization:
    "Letter of Authorization signed by the Director(s) to authorise the use of ipaymy on behalf of your company",
  authorizedPersonIds:
    "Passport/National IDs of all individuals listed in the Letter of Authorization, including all Directors.",
  specimenSignature:
    "Specimen signature of the ipaymy account holder (option to upload your digital signature)"
};

const SHAREHOLDER_NESTED: React.ReactNode[] = [];

export const COUNTRY_REQUIREMENTS: Record<string, RequirementItem[]> = {
  SG: [
    {
      description: (
        <span>
          <a
            href="https://www.bizfile.gov.sg/buy-info/info-products/business-profile"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.link}
          >
            ACRA Bizfile
          </a>{" "}
          (updated at least within the last three (3) months)
        </span>
      )
    },
    { description: REQUIREMENT_TEXTS.businessWebsite },
    { description: REQUIREMENT_TEXTS.operatingAddressProof },
    {
      description: REQUIREMENT_TEXTS.individualShareholderIds,
      nested: SHAREHOLDER_NESTED
    },
    { description: REQUIREMENT_TEXTS.corporateShareholderDocument },
    {
      description: REQUIREMENT_TEXTS.letterOfAuthorization,
      nested: [REQUIREMENT_TEXTS.authorizedPersonIds]
    },
    { description: REQUIREMENT_TEXTS.specimenSignature }
  ],
  AU: [
    {
      description: (
        <span>
          Official Company Extract such as{" "}
          <a
            href="https://share.google/akAoUiXacqo20qdiS"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.link}
          >
            ASIC Extract
          </a>{" "}
          (updated at least within the last three (3) months)
        </span>
      )
    },
    { description: REQUIREMENT_TEXTS.businessWebsite },
    { description: REQUIREMENT_TEXTS.operatingAddressProof },
    {
      description: REQUIREMENT_TEXTS.individualShareholderIds,
      nested: SHAREHOLDER_NESTED
    },
    { description: REQUIREMENT_TEXTS.corporateShareholderDocumentAU },
    {
      description: REQUIREMENT_TEXTS.letterOfAuthorization,
      nested: [REQUIREMENT_TEXTS.authorizedPersonIds]
    },
    { description: REQUIREMENT_TEXTS.specimenSignature }
  ],
  HK: [
    {
      description: (
        <span>
          Official Annual Return document (
          <a
            href="https://share.google/u8smCyq41fl9UiS9O"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.link}
          >
            Form NAR1
          </a>
          ) (updated at least within the last 3 months)
        </span>
      )
    },
    { description: REQUIREMENT_TEXTS.businessWebsite },
    { description: REQUIREMENT_TEXTS.operatingAddressProof },
    {
      description: REQUIREMENT_TEXTS.individualShareholderIds,
      nested: SHAREHOLDER_NESTED
    },
    { description: REQUIREMENT_TEXTS.corporateShareholderDocumentHK },
    {
      description: REQUIREMENT_TEXTS.letterOfAuthorization,
      nested: [REQUIREMENT_TEXTS.authorizedPersonIds]
    },
    { description: REQUIREMENT_TEXTS.specimenSignature }
  ],
  MY: [
    {
      description: (
        <span>
          Official Company Extract such as (
          <a
            href="https://www.ssm.com.my/Pages/Product/PDF/ROC/COMPANY%20PROFILE.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.link}
          >
            SSM Extract
          </a>
          ) (updated at least within 3 months)
        </span>
      )
    },
    { description: REQUIREMENT_TEXTS.businessWebsite },
    { description: REQUIREMENT_TEXTS.operatingAddressProof },
    {
      description: REQUIREMENT_TEXTS.individualShareholderIds,
      nested: SHAREHOLDER_NESTED
    },
    { description: REQUIREMENT_TEXTS.corporateShareholderDocumentMY },
    {
      description: REQUIREMENT_TEXTS.letterOfAuthorization,
      nested: [REQUIREMENT_TEXTS.authorizedPersonIds]
    },
    { description: REQUIREMENT_TEXTS.specimenSignature }
  ]
};
